import { useMemo } from 'react';

type LogoVariant = 'header' | 'header-mobile' | 'footer';

interface LogoProps {
  variant?: LogoVariant;
  className?: string;
  isDark?: boolean;
}

export default function Logo({ variant = 'header', className = '', isDark = false }: LogoProps) {
  const isFooter = variant === 'footer';
  const isMobile = variant === 'header-mobile';

  // Footer is always dark. Otherwise use the isDark prop from parent (header scroll state)
  const darkMode = isFooter || isDark;

  const iconColors = useMemo(() => ({
    bg: '#1E3A47',
    frame: '#FFFFFF',
    sash: '#FF6B4A',
    handle: '#FFFFFF',
  }), []);

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Icon SVG — 48x48 for better visibility */}
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0"
        aria-hidden="true"
      >
        {/* Background square */}
        <rect width="48" height="48" rx="12" fill={iconColors.bg} />

        {/* Outer frame stroke */}
        <rect
          x="8"
          y="8"
          width="32"
          height="32"
          rx="4"
          stroke={iconColors.frame}
          strokeWidth="2"
          fill="none"
        />

        {/* Central mullion */}
        <line x1="24" y1="8" x2="24" y2="40" stroke={iconColors.frame} strokeWidth="2" />

        {/* Left sash — closed */}
        <line x1="8" y1="24" x2="24" y2="24" stroke={iconColors.frame} strokeWidth="2" />
        <line x1="16" y1="8" x2="16" y2="24" stroke={iconColors.frame} strokeWidth="1.5" opacity="0.6" />
        <line x1="8" y1="16" x2="24" y2="16" stroke={iconColors.frame} strokeWidth="1.5" opacity="0.6" />

        {/* Right sash — tilted (oscillo-battant) filled in coral */}
        <path
          d="M24 8 L40 6.5 L40 40 L24 40 Z"
          fill={iconColors.sash}
          opacity="0.95"
        />

        {/* Right sash inner frame */}
        <line x1="24" y1="24" x2="40" y2="24" stroke={iconColors.frame} strokeWidth="2" />
        <line x1="32" y1="6.5" x2="32" y2="24" stroke={iconColors.frame} strokeWidth="1.5" opacity="0.6" />
        <line x1="24" y1="16" x2="40" y2="16" stroke={iconColors.frame} strokeWidth="1.5" opacity="0.6" />
        <line x1="32" y1="24" x2="32" y2="40" stroke={iconColors.frame} strokeWidth="1.5" opacity="0.6" />
        <line x1="24" y1="32" x2="40" y2="32" stroke={iconColors.frame} strokeWidth="1.5" opacity="0.6" />

        {/* Handle on right sash */}
        <rect x="36" y="18" width="2.5" height="9" rx="1.25" fill={iconColors.handle} />
      </svg>

      {/* Text */}
      <div className="flex flex-col leading-none">
        <span
          className="font-extrabold tracking-tight"
          style={{
            color: darkMode ? '#FFFFFF' : '#1E3A47',
            fontFamily: "'Poppins', 'Inter', sans-serif",
            fontSize: isMobile ? '1.1rem' : '1.35rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
          }}
        >
          DF PRO
        </span>
        {!isMobile && (
          <span
            className="font-semibold tracking-wide"
            style={{
              color: '#FF6B4A',
              fontSize: '0.78rem',
              fontFamily: "'Poppins', 'Inter', sans-serif",
              fontWeight: 600,
              letterSpacing: '0.04em',
              marginTop: '2px',
            }}
          >
            Réparation Fenêtre
          </span>
        )}
      </div>
    </div>
  );
}